import React, { useState } from 'react';
import styled from 'styled-components';
import {
    EuiFlyout,
    EuiFlyoutBody,
    EuiFlyoutHeader,
    EuiTitle,
    EuiIcon
} from '@elastic/eui';
import utilsService from '../../../../../servises/utils.service';
import ServerComInfo from '../ServerComInfo';

const ShowDrilldownStyled = styled.div`
  color: #1890ff;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  line-height: 16px;

  &:hover {
    text-decoration: underline;
  }

  svg {
    path {
      fill: #1890ff;
    }
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 12px;
  margin-top: 10px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SectionHeader = styled.div`
  grid-column: span 2;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--color-primary, #6366f1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding-bottom: 6px;
  margin-top: 14px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  
  @media (max-width: 600px) {
    grid-column: span 1;
  }
`;

const AlertBanner = styled.div`
  grid-column: span 2;
  background: rgba(244, 63, 94, 0.1);
  border: 1px solid rgba(244, 63, 94, 0.2);
  color: #f43f5e;
  border-radius: 8px;
  padding: 12px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  
  @media (max-width: 600px) {
    grid-column: span 1;
  }
`;

const CopiableInfo = ({ label, value }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        if (!value) return;
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!value) return null;

    return (
        <ServerComInfo
            name={ label }
            data={ copied ? "Copied!" : value }
            onClick={ handleCopy }
            title={ copied ? "Copied!" : `Click to copy ${value}` }
            style={ copied ? { color: '#48bb78', fontWeight: 'bold' } : {} }
        />
    );
};

const ServerDrilldown = ({ server }) => {
    const [showFlyout, setShowFlyout] = useState(false);

    const ipv4 = utilsService.getFromMetrics(server, 'ipv4');
    const ipv6 = utilsService.getFromMetrics(server, 'ipv6');
    const osName = utilsService.getFromMetrics(server, 'os_name');
    const kernel = utilsService.getFromMetrics(server, 'os_kernel');
    const osArch = utilsService.getFromMetrics(server, 'os_arch');
    const sessions = utilsService.getFromMetrics(server, 'sessions');
    const nicName = utilsService.getFromMetrics(server, 'nic');
    const agentVersion = utilsService.getFromMetrics(server, 'version');

    const hostDetails = utilsService.getFromMetrics(server, 'host_details', null);

    return (
        <>
            <ShowDrilldownStyled onClick={ () => setShowFlyout(true) }>
                Details
                <EuiIcon type="inspect" size="s" style={{ alignSelf: 'center' }} />
            </ShowDrilldownStyled>

            { showFlyout && (
                <EuiFlyout
                    size="l"
                    ownFocus={true}
                    outsideClickCloses={true}
                    onClose={ () => setShowFlyout(false) }
                >
                    <EuiFlyoutHeader hasBorder>
                        <EuiTitle size="m">
                            <h2>Server Info Dashboard</h2>
                        </EuiTitle>
                    </EuiFlyoutHeader>
                    <EuiFlyoutBody>
                        { hostDetails?.reboot_required && (
                            <AlertBanner>
                                <EuiIcon type="alert" size="m" />
                                <span>Reboot Required: A system reboot is pending to apply security updates.</span>
                            </AlertBanner>
                        ) }

                        <ContentGrid>
                            <SectionHeader>
                                <EuiIcon type="securitySignal" size="s" /> General Host Metrics
                            </SectionHeader>
                            <ServerComInfo name="Server Name" data={ server.name } />
                            <ServerComInfo name="Server Added" data={ server.serverData.createdAt } />
                            { hostDetails?.hostname && <CopiableInfo label="Hostname" value={ hostDetails.hostname } /> }
                            { hostDetails?.timezone && <ServerComInfo name="Timezone" data={ hostDetails.timezone } /> }
                            { sessions !== undefined && <ServerComInfo name="Active Sessions" data={ sessions } /> }
                            { agentVersion && <ServerComInfo name="Agent Installed Version" data={ agentVersion } /> }

                            <SectionHeader>
                                <EuiIcon type="logoTerminal" size="s" /> Operating System & Kernel
                            </SectionHeader>
                            { osName && <ServerComInfo name="OS Name" data={ osName } /> }
                            { kernel && <ServerComInfo name="Kernel Version" data={ kernel } /> }
                            { osArch && <ServerComInfo name="Architecture" data={ osArch } /> }

                            <SectionHeader>
                                <EuiIcon type="globe" size="s" /> Network Interface
                            </SectionHeader>
                            { nicName && <ServerComInfo name="NIC Name" data={ nicName } /> }
                            { ipv4 && <CopiableInfo label="IPv4 Address" value={ ipv4 } /> }
                            { ipv6 && <CopiableInfo label="IPv6 Address" value={ ipv6 } /> }

                            { hostDetails && (hostDetails.virtualization || hostDetails.container || hostDetails.cloud_vendor || hostDetails.boot_mode || hostDetails.package_manager) && (
                                <>
                                    <SectionHeader>
                                        <EuiIcon type="management" size="s" /> Platform & Environment
                                    </SectionHeader>
                                    { hostDetails.virtualization && <ServerComInfo name="Virtualization System" data={ hostDetails.virtualization } /> }
                                    { hostDetails.container && <ServerComInfo name="Containerization" data={ hostDetails.container } /> }
                                    { hostDetails.cloud_vendor && <ServerComInfo name="Cloud Provider" data={ hostDetails.cloud_vendor } /> }
                                    { hostDetails.boot_mode && <ServerComInfo name="Boot Mode" data={ hostDetails.boot_mode } /> }
                                    { hostDetails.package_manager && <ServerComInfo name="System Package Manager" data={ hostDetails.package_manager } /> }
                                </>
                            ) }
                        </ContentGrid>
                    </EuiFlyoutBody>
                </EuiFlyout>
            ) }
        </>
    );
};

export default ServerDrilldown;
